import { isSensitivePath } from './privacy.js';
import { canMeasureGoogle, hasGoogleMeasurementPermission } from './googleMeasurementConsent.js';
const CAMPAIGN_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'gclid', 'gbraid', 'wbraid'];
const STORAGE_KEY = 'tda_campaign_attribution';

function readCampaign(params) {
  return Object.fromEntries(CAMPAIGN_KEYS.map(key => {
    const values = params.getAll(key);
    // Prefer platform IDs if a campaign template appends a second UTM value.
    const platformId = key === 'utm_campaign' ? values.find(v => /^cmpn_[a-z0-9]+$/i.test(v))
      : key === 'utm_content' ? values.find(v => /^ad_[a-z0-9]+$/i.test(v)) : undefined;
    const value = platformId || values[0] || '';
    return [key, /^[a-z0-9_.-]{1,200}$/i.test(value) ? value : ''];
  }));
}

// Read only the existing OpenAI attribution cookie, without decoding or logging it.
// Contact data and browser identifiers are not included in this context.
export function getOpenAILeadContext() {
  try {
    if (window.location.origin !== 'https://trinitydentalarts.com' ||
        isSensitivePath() || navigator.globalPrivacyControl === true ||
        navigator.doNotTrack === '1' ||
        localStorage.getItem('tda_cookie_consent_v2') !== 'granted' ||
        localStorage.getItem('tda_ad_measurement_consent_v2') !== 'granted') return {};
    const cookie = document.cookie.split(';').map(part => part.trimStart()).find(part => part.startsWith('__oppref='));
    const token = cookie ? cookie.slice('__oppref='.length) : '';
    if (!token || token.length > 4096 || /[\s\x00-\x1f\x7f]/.test(token)) return {};
    return { openai_ad_consent: 'granted', openai_oppref: token };
  } catch { return {}; }
}

// Keep consented campaign attribution across internal navigation for this session.
// Store only the allowlisted campaign fields, never quiz answers or patient data.
export function captureLeadAttribution() {
  try {
    if (!hasGoogleMeasurementPermission()) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    // A gallery/intake route must not erase the allowed landing-page source.
    // It also must not capture new query data or expose saved attribution there.
    if (!canMeasureGoogle() || isSensitivePath()) return;
    const params = new URLSearchParams(window.location.search);
    if (CAMPAIGN_KEYS.some(key => params.has(key))) {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(readCampaign(params)));
    }
  } catch { /* Storage may be blocked; navigation and inquiries still work. */ }
}

export function getLeadTracking() {
  if (typeof window === 'undefined') return {};
  captureLeadAttribution();
  if (!canMeasureGoogle()) return {};
  const params = new URLSearchParams(window.location.search);
  let campaign = readCampaign(params);
  if (!CAMPAIGN_KEYS.some(key => params.has(key))) {
    try {
      const saved = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '{}');
      campaign = Object.fromEntries(CAMPAIGN_KEYS.map(key => [
        key, typeof saved?.[key] === 'string' && /^[a-z0-9_.-]{1,200}$/i.test(saved[key]) ? saved[key] : '',
      ]));
    } catch { /* Invalid or unavailable storage: use empty attribution. */ }
  }
  return { ...campaign, measurement_consent: 'granted', source_page: window.location.pathname, referrer: (() => { try { return new URL(document.referrer).origin; } catch { return ''; } })() };
}

